import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { CUSTOM_PRESENTER_FILE_INPUT_ID } from '@/constants/e2eSelectors';
import differenceVideo from '@/images/avatars/differenceVideo.mp4';
import photoCardVideo from '@/images/avatars/photo_card_preview.mp4';
import photoCardImage from '@/images/avatars/photo_card_preview.png';
import videoCardVideo from '@/images/avatars/video_card_preview.mp4';
import videoCardImage from '@/images/avatars/video_card_preview.png';
import { useUploadAvatar, useValidateUserCapping } from '@/pages/avatar';
import {
    UseUploadAvatarProps,
    useCreateAvatarRedirect,
    useCreateExpressAvatarRedirect,
    useCreatePremiumPlusAvatarRedirect,
} from '@/pages/avatar/hooks';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { PlanGroup, Presenter, PresenterUsecase } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, DialogTitle } from '@mui/material';
import { useRef, useState } from 'react';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import { AvatarType } from '../../common';
import { UpgradeModal } from '../AvatarsUpgradeModal';
import { CardInterface, CreateAvatarCard } from './CreateAvatarCard';

type CreateAvatarModalUploadAvatarProps = Omit<UseUploadAvatarProps, 'onUpload'> & { hideTalks?: boolean };

export const createAvatarModalAtom = atom<CreateAvatarModalUploadAvatarProps & { isOpen: boolean }>({
    key: 'create-avatar-modal',
    default: { isOpen: false },
});

export const useOpenCreateAvatarModal = () => {
    const set = useSetRecoilState(createAvatarModalAtom);
    return (props: CreateAvatarModalUploadAvatarProps) => set({ ...props, isOpen: true });
};

const StyledDialogContent = styled(DialogContent)`
    margin-top: 8px;

    &.MuiDialogContent-root {
        display: flex;
        flex-direction: row;
        gap: 24px;
        margin-bottom: 24px;
        overflow: visible;
        height: 330px;
        align-items: center;
    }
`;

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        color: ${theme.colors.secondary[900]};
        font-size: 24px;
        font-weight: 600;
        letter-spacing: -0.48px;
        padding-block-end: 2px;
    }
`;

const CloseButton = styled(IconButton)`
    margin-left: auto;
    color: ${theme.colors.black[800]};

    svg {
        width: 24px;
        height: 24px;
    }
`;

interface Props {
    from: string;
    onUpload?: (avatar?: Presenter) => void;
}

export const CreateAvatarModal = ({ from, onUpload }: Props) => {
    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect, user } = useAuth();

    const [{ isOpen, ...uploadAvatarProps }, setCreateAvatarModal] = useRecoilState(createAvatarModalAtom);
    const { useCase } = uploadAvatarProps;

    const { isLoading: isValidatingExpressAvatar } = useValidateUserCapping();
    const isDirectExpressFlowEnabled = useFlag('direct_express_flow_enabled', false);
    const { onClick: onCreateAvatar } = useCreateAvatarRedirect();
    const { onClick: onCreateExpressAvatar } = useCreateExpressAvatarRedirect();
    const { onClick: onCreatePremiumPlusAvatar } = useCreatePremiumPlusAvatarRedirect();

    const fileInputId = CUSTOM_PRESENTER_FILE_INPUT_ID;
    const { loading, onUploadClick, onGetFile } = useUploadAvatar({
        ...uploadAvatarProps,
        onUpload,
        inputId: fileInputId,
        isFromWorkspaceCTA: from === 'workspace-cta',
    });

    const [isPremiumSelected] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const isTrialUser = user?.planGroup === PlanGroup.TRIAL;

    const handleClose = () => {
        setCreateAvatarModal({ isOpen: false });
        setShowUpgradeModal(false);
    };

    const handleUpload = async (e: React.MouseEvent<HTMLDivElement, MouseEvent> | File, isInput: boolean = true) => {
        isInput ? onUploadClick(e) : onGetFile(e);
        handleClose();
    };

    const isAgentUseCase = useCase === PresenterUsecase.Agent;
    const handleCreate = async () => {
        if (isAnonymous) {
            loginWithRedirect();
        }

        if (isAgentUseCase && isTrialUser) {
            setShowUpgradeModal(true);
            return;
        }

        handleClose();

        if (isAgentUseCase) {
            return onCreatePremiumPlusAvatar({ from });
        }

        return isDirectExpressFlowEnabled ? onCreateExpressAvatar({ from }) : onCreateAvatar({ from });
    };

    const cards: CardInterface[] = [
        {
            id: 'create-avatar-with-photo-card',
            testId: 'create-avatar-with-photo-card',
            image: photoCardImage,
            video: photoCardVideo,
            title: 'Create with a photo',
            description: 'Quick and easy. Upload a photo to create talking head videos.',
            onGetFile: (file: React.MouseEvent<HTMLDivElement, MouseEvent> | File) => {
                mixpanel?.track('avatar-create', {
                    type: 'photo',
                    event: 'click',
                    from,
                });
                return handleUpload(file, false);
            },
            ...(!isAgentUseCase
                ? {
                      streaming: 'Streaming supported',
                      streamingTooltip: 'Standard Avatars are available on Agents.',
                  }
                : {}),
            onClick: (file: React.MouseEvent<HTMLDivElement, MouseEvent> | File) => {
                mixpanel?.track('avatar-create', {
                    type: 'photo',
                    event: 'click',
                    from,
                });
                return handleUpload(file);
            },
            isLoading: loading,
            isDisabled: loading,
        },
        {
            id: 'create-avatar-with-video-card',
            testId: 'create-avatar-with-video-card',
            image: videoCardImage,
            video: videoCardVideo,
            title: 'Create with a video',
            description: 'For higher quality results. Upload or record video footage.',
            quality: 'High quality',
            onClick: () => {
                mixpanel?.track('avatar-create', {
                    type: AvatarType.Express,
                    event: 'click',
                    from,
                });
                return handleCreate();
            },
            isDisabled: loading || isValidatingExpressAvatar,
            isLoading: isValidatingExpressAvatar,
        },
    ];

    const [showVideoPopup, setShowVideoPopup] = useState(false);
    const popupTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
        if (popupTimeout.current) {
            clearTimeout(popupTimeout.current);
            popupTimeout.current = null;
        }
        setShowVideoPopup(true);
    };

    const handleMouseLeave = () => {
        popupTimeout.current = setTimeout(() => {
            setShowVideoPopup(false);
        }, 10);
    };

    return (
        <>
            <input id={fileInputId} type="file" accept="image/*" style={{ display: 'none' }} />
            <BootstrapDialog open={isOpen} onClose={handleClose}>
                <StyledDialogTitle>
                    Create your Avatar
                    <CloseButton ariaLabel="close" onClick={handleClose}>
                        <CloseIcon />
                    </CloseButton>
                </StyledDialogTitle>
                <div style={{ padding: '20px', position: 'relative' }}>
                    {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} isPremiumPlus={isPremiumSelected} />}
                    {showVideoPopup && (
                        <div
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            style={{
                                position: 'fixed',
                                top: '53.5%',
                                left: '38%',
                                transform: 'translate(-50%, -50%)',
                                width: '640px',
                                height: '360px',
                                backgroundColor: 'black',
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                zIndex: 1500,
                                overflow: 'hidden',
                            }}>
                            <video
                                src={differenceVideo}
                                autoPlay
                                muted
                                loop
                                style={{
                                    width: '640px',
                                    height: '360px',
                                    objectFit: 'contain',
                                }}
                            />
                        </div>
                    )}
                </div>
                <StyledDialogContent>
                    {cards.map((card, index) => (
                        <CreateAvatarCard id={card.id} key={index} card={card} />
                    ))}
                </StyledDialogContent>
            </BootstrapDialog>
        </>
    );
};
